import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Edit, Search, AccountTree } from '@mui/icons-material';

import {
  COST_AREA_OPTIONS,
  COST_BEHAVIOR_OPTIONS,
  COST_NATURE_OPTIONS,
  getOptionLabel,
} from '../constants/financeConstants';
import {
  ActionIconButton,
  ConfirmationDialog,
  CustomAutoComplete,
  CustomButton,
  CustomTextField,
  FormTitleBox,
  GeneralTableBox,
  HeadTableCell,
  HeaderFilterBox,
  NoDataSkeleton,
  TableBodySkeleton,
} from '../../common/components';
import {
  useAccountingAccounts,
  useCreateAccountingAccount,
  useDeleteAccountingAccount,
  useUpdateAccountingAccount,
} from '../../hooks/useAccountingAccounts';
import { useMovementConcepts } from '../../hooks/useMovementConcepts';

const emptyForm = {
  code: '',
  name: '',
  description: '',
  default_cost_behavior: 'fixed',
  default_cost_nature: 'ordinary',
  default_cost_area: 'work',
  default_uuid_movement_concept: '',
  is_active: true,
};

const normalizeAccountForm = (account = null) => ({
  ...emptyForm,
  code: account?.code || '',
  name: account?.name || '',
  description: account?.description || '',
  default_cost_behavior: account?.default_cost_behavior || 'fixed',
  default_cost_nature: account?.default_cost_nature || account?.default_cost_traceability || 'ordinary',
  default_cost_area: account?.default_cost_area || 'work',
  default_uuid_movement_concept: account?.default_uuid_movement_concept || '',
  is_active: account?.is_active === undefined ? true : Boolean(account.is_active),
});

const AccountingAccountDialog = ({
  account,
  movementConceptOptions,
  onClose,
  onSave,
  open,
  saving,
}) => {
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setForm(normalizeAccountForm(account));
      setError('');
    }
  }, [account, open]);

  const handleChange = (event) => {
    const { name, value, checked, type } = event.target;
    setForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      setError('El nombre es obligatorio.');
      return;
    }

    try {
      setError('');
      await onSave(form);
    } catch (saveError) {
      setError(saveError?.response?.data?.error || saveError?.response?.data?.message || saveError.message);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>{account ? 'Editar cuenta contable' : 'Nueva cuenta contable'}</DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <Stack spacing={1.5}>
          {error && <Alert severity="error">{error}</Alert>}
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '160px minmax(0, 1fr)' }, gap: 1.5 }}>
            <CustomTextField
              label="Código"
              name="code"
              value={form.code}
              onChange={handleChange}
            />
            <CustomTextField
              label="Nombre"
              name="name"
              value={form.name}
              onChange={handleChange}
              required
            />
          </Box>
          <CustomTextField
            label="Descripción"
            name="description"
            value={form.description}
            onChange={handleChange}
          />
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' }, gap: 1.5 }}>
            <CustomAutoComplete
              label="Fijo/Variable"
              name="default_cost_behavior"
              value={form.default_cost_behavior}
              onChange={handleChange}
              options={COST_BEHAVIOR_OPTIONS}
            />
            <CustomAutoComplete
              label="Ordinario/Extraordinario"
              name="default_cost_nature"
              value={form.default_cost_nature}
              onChange={handleChange}
              options={COST_NATURE_OPTIONS}
            />
            <CustomAutoComplete
              label="Área"
              name="default_cost_area"
              value={form.default_cost_area}
              onChange={handleChange}
              options={COST_AREA_OPTIONS}
            />
          </Box>
          <CustomAutoComplete
            label="Concepto por defecto"
            name="default_uuid_movement_concept"
            value={form.default_uuid_movement_concept}
            onChange={handleChange}
            options={movementConceptOptions}
            placeholder="Sin concepto"
          />
          <FormControlLabel
            control={(
              <Switch
                checked={Boolean(form.is_active)}
                name="is_active"
                onChange={handleChange}
              />
            )}
            label="Cuenta activa"
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <CustomButton variant="outlined" onClick={onClose}>Cancelar</CustomButton>
        <CustomButton variant="base" onClick={handleSave} isLoading={saving}>
          {account ? 'Guardar cambios' : 'Crear cuenta'}
        </CustomButton>
      </DialogActions>
    </Dialog>
  );
};

const AccountingAccounts = () => {
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [accountToDelete, setAccountToDelete] = useState(null);

  const filters = useMemo(() => ({
    search: search || undefined,
  }), [search]);

  const { data: accounts = [], isLoading, error } = useAccountingAccounts(filters);
  const { data: movementConcepts = [] } = useMovementConcepts();
  const createMutation = useCreateAccountingAccount();
  const updateMutation = useUpdateAccountingAccount();
  const deleteMutation = useDeleteAccountingAccount();

  const movementConceptOptions = useMemo(
    () => movementConcepts
      .filter((concept) => concept.is_active !== false)
      .map((concept) => ({ value: concept.uuid, label: concept.code ? `${concept.name} · ${concept.code}` : concept.name })),
    [movementConcepts],
  );

  const handleCreate = () => {
    setSelectedAccount(null);
    setDialogOpen(true);
  };

  const handleEdit = (account) => {
    setSelectedAccount(account);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedAccount(null);
  };

  const handleSave = async (form) => {
    if (selectedAccount) {
      await updateMutation.mutateAsync({ id: selectedAccount.uuid, data: form });
    } else {
      await createMutation.mutateAsync(form);
    }
    handleCloseDialog();
  };

  const handleDelete = (account) => {
    setAccountToDelete(account);
  };

  const handleConfirmDelete = async () => {
    if (!accountToDelete) return;
    try {
      await deleteMutation.mutateAsync(accountToDelete.uuid);
    } finally {
      setAccountToDelete(null);
    }
  };

  return (
    <Box>
      <FormTitleBox
        title="Finanzas | Cuentas contables"
        showButton
        buttonText="Nueva cuenta"
        buttonIcon={AccountTree}
        onButtonClick={handleCreate}
      />

      <HeaderFilterBox variant="oneRow">
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center', width: '100%' }}>
          <CustomTextField
            label="Buscar"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            size="medium"
            sx={{ width: { xs: '100%', sm: 320 } }}
            InputProps={{
              startAdornment: <Search sx={{ color: 'primary.main', mr: 1 }} />,
            }}
          />
        </Box>
      </HeaderFilterBox>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          No se pudieron cargar las cuentas contables.
        </Alert>
      )}

      <GeneralTableBox>
        <Table aria-label="accounting accounts table">
          <TableHead>
            <TableRow>
              <HeadTableCell>Código</HeadTableCell>
              <HeadTableCell>Nombre</HeadTableCell>
              <HeadTableCell>Clasificación por defecto</HeadTableCell>
              <HeadTableCell>Concepto</HeadTableCell>
              <HeadTableCell>Estado</HeadTableCell>
              <HeadTableCell align="center">Acciones</HeadTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableBodySkeleton rows={6} columns={6} />
            ) : accounts.length ? (
              accounts.map((account) => (
                <TableRow key={account.uuid}>
                  <TableCell sx={{ fontWeight: 800 }}>{account.code || '-'}</TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 800 }}>
                      {account.name}
                    </Typography>
                    {account.description && (
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        {account.description}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {[
                      getOptionLabel(COST_BEHAVIOR_OPTIONS, account.default_cost_behavior),
                      getOptionLabel(COST_NATURE_OPTIONS, account.default_cost_nature),
                      getOptionLabel(COST_AREA_OPTIONS, account.default_cost_area),
                    ].filter(Boolean).join(' / ')}
                  </TableCell>
                  <TableCell>{account.default_movement_concept_name || '-'}</TableCell>
                  <TableCell>{account.is_active ? 'Activa' : 'Inactiva'}</TableCell>
                  <TableCell align="center">
                    <Stack direction="row" justifyContent="center" spacing={0.5}>
                      <IconButton size="small" onClick={() => handleEdit(account)} aria-label="Editar cuenta contable">
                        <Edit fontSize="small" />
                      </IconButton>
                      <ActionIconButton
                        variant="delete"
                        onClick={() => handleDelete(account)}
                        disabled={deleteMutation.isPending || account.is_system}
                        aria-label="Eliminar cuenta contable"
                      />
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <NoDataSkeleton colSpan={6} message="No se encontraron cuentas contables" />
            )}
          </TableBody>
        </Table>
      </GeneralTableBox>

      <AccountingAccountDialog
        account={selectedAccount}
        movementConceptOptions={movementConceptOptions}
        onClose={handleCloseDialog}
        onSave={handleSave}
        open={dialogOpen}
        saving={createMutation.isPending || updateMutation.isPending}
      />

      <ConfirmationDialog
        open={Boolean(accountToDelete)}
        onClose={() => setAccountToDelete(null)}
        onConfirm={handleConfirmDelete}
        title="Eliminar cuenta contable"
        message={
          accountToDelete
            ? `¿Confirmás eliminar la cuenta contable "${accountToDelete.name}"? Dejará de estar disponible para facturas y asientos.`
            : '¿Confirmás eliminar la cuenta contable seleccionada?'
        }
        isLoading={deleteMutation.isPending}
      />
    </Box>
  );
};

export default AccountingAccounts;
